import { useCallback, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import Utils from '../../../../utils/utils'
import { appError } from '../../../../redux/slices/app'
import { Store } from '../../../../redux/store'
import { getStore } from '../../../../redux/storeHolder'
import { buildCreateAccountEvent, buildSigninEvent, SubmittedCode, SubmittedStatus } from '../../../../utils/floodlightEvents'
import appRoutes from '../../../../utils/routes'
import {
    startSocialLogin,
    listenForSocialLoginCallback,
    SocialLoginCancelled,
    SocialLoginProviderCancelled,
    SocialProvider,
    clearSocialLoginSession,
} from '../../../../services/keycloakAuth'
import { useCompleteSocialLogin } from './useCompleteSocialLogin'

export function useSocialLoginHandler() {
    const dispatch = useDispatch()
    const { t } = useTranslation(['account'])
    const completeSocialLogin = useCompleteSocialLogin()
    const [socialLoading, setSocialLoading] = useState(false)
    const popupRef = useRef<Window | null>(null)
    const { loggedIn, language, userProfile, userIP } = useSelector((s: Store) => s.App)

    const handleSocialLogin = useCallback(async (provider: SocialProvider) => {
        try { sessionStorage.setItem('tm_signup_provider', provider) } catch {}
        const onCreatePage = window.location.pathname.replace(/\/+$/, '') === appRoutes.account.createAccount.live
        const buildEventInput = () => ({
            referrer: document.referrer,
            loggedIn,
            language,
            country: userProfile?.country?.code2 || userIP?.country_code || '',
            portalHost: window.location.hostname,
            pathname: window.location.pathname,
        })
        const pushSocialClick = () => {
            window.dataLayer?.push(
                onCreatePage
                    ? buildCreateAccountEvent('submitted', { ...buildEventInput(), provider })
                    : buildSigninEvent('submitted', { ...buildEventInput(), provider, status: 'clicked', code: 'na' })
            )
        }
        const pushSocialLoginResult = (status: SubmittedStatus, code: SubmittedCode) => {
            if (onCreatePage) return
            // On success the closure userProfile is stale (captured pre-login), so read
            // fresh state from the store the same way the error event does.
            const app = status === 'success' ? getStore()?.getState().App : undefined
            window.dataLayer?.push(buildSigninEvent('submitted', {
                ...buildEventInput(),
                provider,
                status,
                code,
                userId: app?.userProfile?.id ?? null,
                accountId: app?.liveAccounts?.[0]?.account?.accountNumber ?? null,
                email: app?.userProfile?.email ?? null,
            }))
        }
        pushSocialClick()
        // Refuse re-entry while a popup is still open — second click just refocuses and bails,
        // so we never overlap PKCE state or attach two listeners.
        if (popupRef.current) {
            try { popupRef.current.focus() } catch { /* COOP may block focus — ignore */ }
            return
        }
        setSocialLoading(true)
        let popup: Window | null = null
        try {
            popup = startSocialLogin(provider)
        } catch {
            setSocialLoading(false)
            return
        }

        if (!popup) return
        popupRef.current = popup

        let success = false
        try {
            const { code, state } = await listenForSocialLoginCallback(popup)
            await completeSocialLogin({ code, state })
            success = true
            pushSocialLoginResult('success', 'na')
        } catch (err) {
            clearSocialLoginSession()
            // All cancel flavours (popup X'd, provider-side) are silent — user knows they cancelled.
            if (err instanceof SocialLoginCancelled || err instanceof SocialLoginProviderCancelled) return
            console.error('Social login failed:', err)
            pushSocialLoginResult('failed', 'na')
            dispatch(appError(Utils.customError(t('account:login.socialLoginFailed'))))
        } finally {
            popupRef.current = null
            // On success, leave the loader up: the preload saga finishes asynchronously
            // and App routes via the bootCompletionCount effect, so resetting here flashes
            // the bare login page until history.push(dashboard) fires. Navigation unmounts us.
            if (!success) setSocialLoading(false)
        }
    }, [completeSocialLogin, dispatch, t, loggedIn, language, userProfile, userIP])

    return { socialLoading, handleSocialLogin }
}
